export const returnNullIfEmptyString = (
  inputString: string | null | undefined
) => (!inputString || inputString === "" ? null : inputString);

export function truncateStringWithEllipsis(
  str: string | null | undefined,
  maxLength: number
): string | null {
  if (!str || str === null) {
    return null;
  }

  if (str.length <= maxLength) {
    return str;
  }

  return str.slice(0, maxLength) + "...";
}

export const applySecurityMask = (
  str: string | null | undefined,
  maskLength: number = str?.length || 0,
  maskType: "start" | "middle" | "end" = "start",
  maskCharacter: string = "*"
): string | null | undefined => {
  if (!str) return str;

  maskLength = Math.floor(Math.min(str.length, maskLength));

  // Create the mask string of the required length
  const mask = Array(maskLength).fill(maskCharacter).join("");

  if (maskType === "start") {
    // Mask the start of the string
    const secondSection = str.slice(maskLength);
    return mask + secondSection;
  } else if (maskType === "end") {
    // Mask the end of the string
    const firstSection = str.slice(0, str.length - maskLength);
    return firstSection + mask;
  } else if (maskType === "middle") {
    // Mask the middle of the string
    // Calculate start and end positions for the mask
    const totalUnmaskedLength = str.length - maskLength;
    const startUnmaskedLength = Math.ceil(totalUnmaskedLength / 2);
    const endUnmaskedLength = totalUnmaskedLength - startUnmaskedLength;

    const firstSection = str.slice(0, startUnmaskedLength);
    const lastSection = str.slice(str.length - endUnmaskedLength);

    return firstSection + mask + lastSection;
  }

  // Default fallback (shouldn't reach here due to type safety)
  return str;
};

export const renderBooleanAsText = (booleanValue: boolean): string =>
  booleanValue ? "Yes" : "No";

export const generateRandomString = (length: number): string => {
  let randomString = "";
  const characters =
    "ABCDEFGHJKMNOPQRSTUVWXYZabcdefghjkmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;

  for (let i = 0; i < length; i++) {
    randomString += characters.charAt(
      Math.floor(Math.random() * charactersLength)
    );
  }

  return randomString;
};

export const sanitizeStringForUrl = (
  inputString: string,
  replacementChar: string = "",
  allowedCharsPattern: RegExp = /[^@._-|]+/g
): string => {
  return inputString.replace(allowedCharsPattern, replacementChar);
};

/**
 * Converts a camelCase string to a normal capitalized string
 * @param camelCaseStr - The camelCase string to convert
 * @returns A capitalized string with spaces between words
 */
export function camelCaseToCapitalized(camelCaseStr: string): string {
  if (!camelCaseStr) return "";

  return (
    camelCaseStr
      // Insert space before any uppercase letter that follows a lowercase letter
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      // Insert space before any uppercase letter that is followed by lowercase letters
      .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
      // Capitalize the first letter and make the rest lowercase
      .replace(/^./, (str) => str.toUpperCase())
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase())
      // Capitalize the first letter of each word
      .replace(/\s+(.)/g, (match, letter) => ` ${letter.toUpperCase()}`)
  );
}

// Alternative simpler version that works well for most cases
export function simpleCamelCaseToCapitalized(camelCaseStr: string): string {
  if (!camelCaseStr) return "";

  return (
    camelCaseStr
      // Add space before uppercase letters
      .replace(/([A-Z])/g, " $1")
      // Trim any leading space and capitalize first letter
      .trim()
      .replace(/^./, (str) => str.toUpperCase())
  );
}
